import { Fragment } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import Map from '../components/Map';
import Filter from '../components/Filter';
import Search from '../components/Search';
import List from '../components/List';
import Loading from '../components/Loading';
import ConsentGate from '../components/ConsentGate';

const LOCATION_TYPE_QUERY_PARAM = 'Standortart';
const SERVICE_QUERY_PARAM = 'Leistungen';
const FILTER_QUERY_PARAM_KEYS = new Set([LOCATION_TYPE_QUERY_PARAM, SERVICE_QUERY_PARAM]);

const parseFilterValues = value =>
  (value || '')
    .split(',')
    .map(item => item.trim())
    .filter(Boolean)
    .filter((item, index, items) => items.indexOf(item) === index);

const getInitialFiltersFromUrl = () => {
  if (typeof window === 'undefined') {
    return {
      locationTypes: [],
      services: [],
    };
  }

  const params = new URLSearchParams(window.location.search);

  return {
    locationTypes: parseFilterValues(params.get(LOCATION_TYPE_QUERY_PARAM)),
    services: parseFilterValues(params.get(SERVICE_QUERY_PARAM)),
  };
};

const serializeQueryParams = params =>
  Array.from(params.entries())
    .map(([key, value]) => {
      const encodedKey = encodeURIComponent(key);
      const encodedValue = FILTER_QUERY_PARAM_KEYS.has(key)
        ? value
            .split(',')
            .map(item => encodeURIComponent(item))
            .join(',')
        : encodeURIComponent(value);

      return `${encodedKey}=${encodedValue}`;
    })
    .join('&');

const updateFilterQueryParam = (params, key, values) => {
  if (values.length === 0) {
    params.delete(key);
    return;
  }

  params.set(key, values.join(','));
};

const areArraysEqual = (left, right) =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const LocationView = ({ locationsUrl, accessToken, consentNote }) => {
  const initialFilters = getInitialFiltersFromUrl();
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocationTypes, setSelectedLocationTypes] = useState(initialFilters.locationTypes);
  const [selectedServices, setSelectedServices] = useState(initialFilters.services);
  const [locationTypes, setLocationTypes] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [availableLocationTypes, setAvailableLocationTypes] = useState([]);
  const [availableServices, setAvailableServices] = useState([]);
  const [hasInitializedFilters, setHasInitializedFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const itemsPerPage = 20;

  // load locations
  useEffect(() => {
    setIsLoading(true);
    fetch(locationsUrl)
      .then(res => res.json())
      .then(data => {
        let items = data?.items || [];
        items = [...items].sort((a, b) => (a?.name || '').localeCompare(b?.name || '', 'de'));
        setLocations(items);
        setFilteredLocations(items);

        // extract unique locationTypes and services
        const locationTypes = [...new Set(items.flatMap(loc => loc.locationTypes || []).filter(Boolean))].sort((a, b) =>
          a.localeCompare(b, 'de')
        );
        const allServices = [...new Set(items.flatMap(loc => loc.services || []).filter(Boolean))].sort((a, b) =>
          a.localeCompare(b, 'de')
        );
        setLocationTypes(locationTypes);
        setAllServices(allServices);
        setAvailableLocationTypes(locationTypes);
        setAvailableServices(allServices);
        setIsLoading(false);
      })
      .catch(err => {
        console.error('error loading locations:', err);
        setIsLoading(false);
      });
  }, [locationsUrl]);

  useEffect(() => {
    if (!hasInitializedFilters) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    updateFilterQueryParam(params, LOCATION_TYPE_QUERY_PARAM, selectedLocationTypes);
    updateFilterQueryParam(params, SERVICE_QUERY_PARAM, selectedServices);

    const queryString = serializeQueryParams(params);
    const nextUrl = `${window.location.pathname}${queryString ? `?${queryString}` : ''}${window.location.hash}`;

    window.history.replaceState(window.history.state, '', nextUrl);
  }, [hasInitializedFilters, selectedLocationTypes, selectedServices]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    setSelectedLocationTypes(prevSelectedLocationTypes => {
      const nextSelectedLocationTypes = prevSelectedLocationTypes.filter(locationType =>
        locationTypes.includes(locationType)
      );

      return areArraysEqual(prevSelectedLocationTypes, nextSelectedLocationTypes)
        ? prevSelectedLocationTypes
        : nextSelectedLocationTypes;
    });

    setSelectedServices(prevSelectedServices => {
      const nextSelectedServices = prevSelectedServices.filter(service => allServices.includes(service));

      return areArraysEqual(prevSelectedServices, nextSelectedServices) ? prevSelectedServices : nextSelectedServices;
    });

    setHasInitializedFilters(true);
  }, [isLoading, locationTypes, allServices]);

  // combine filter and search
  useEffect(() => {
    let filtered = [...locations];

    if (selectedLocationTypes.length > 0) {
      filtered = filtered.filter(
        location =>
          location.locationTypes &&
          selectedLocationTypes.every(locationType => location.locationTypes.includes(locationType))
      );
    }

    if (selectedServices.length > 0) {
      filtered = filtered.filter(
        location => location.services && selectedServices.every(service => location.services.includes(service))
      );
    }

    if (searchTerm && searchTerm.length >= 2) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        location =>
          location.name?.toLowerCase().includes(term) ||
          location.city?.toLowerCase().startsWith(term) ||
          String(location.zip || '').toLowerCase().startsWith(term)
      );

      filtered = [...filtered].sort((a, b) => (a?.name || '').localeCompare(b?.name || '', 'de'));
    }

    setFilteredLocations(filtered);
    setCurrentPage(1);

    const reducedServices = [
      ...new Set([...selectedServices, ...filtered.flatMap(loc => loc.services || []).filter(Boolean)]),
    ].sort((a, b) => a.localeCompare(b, 'de'));
    setAvailableServices(reducedServices);

    const reducedLocationTypes = [
      ...new Set([...selectedLocationTypes, ...filtered.flatMap(loc => loc.locationTypes || []).filter(Boolean)]),
    ].sort((a, b) => a.localeCompare(b, 'de'));
    setAvailableLocationTypes(reducedLocationTypes);
  }, [locations, selectedLocationTypes, selectedServices, searchTerm]);

  const handleSearch = e => {
    setSearchTerm(e.target.value);
  };

  const handleReset = () => {
    setSearchTerm('');
    setSelectedLocationTypes([]);
    setSelectedServices([]);
  };

  const handleLocationTypeChange = locationType => {
    setSelectedLocationTypes(prev =>
      prev.includes(locationType) ? prev.filter(t => t !== locationType) : [...prev, locationType]
    );
  };

  const handleServiceChange = service => {
    setSelectedServices(prev => (prev.includes(service) ? prev.filter(s => s !== service) : [...prev, service]));
  };

  // pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems =
    filteredLocations && filteredLocations.length > 0 ? filteredLocations.slice(indexOfFirstItem, indexOfLastItem) : [];
  const totalPages = Math.ceil((filteredLocations?.length || 0) / itemsPerPage);

  const handlePageChange = newPage => {
    setCurrentPage(newPage);
    document.querySelector('#uberall-locations__list')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Fragment>
      {isLoading ? (
        <Loading />
      ) : (
        <Fragment>
          <ConsentGate consentNote={consentNote}>
            <Map filteredLocations={filteredLocations} accessToken={accessToken} />
          </ConsentGate>
          <Filter
            locationTypes={availableLocationTypes}
            services={availableServices}
            selectedLocationTypes={selectedLocationTypes}
            selectedServices={selectedServices}
            onLocationTypeChange={handleLocationTypeChange}
            onServiceChange={handleServiceChange}
          />
          <Search searchTerm={searchTerm} onSearch={handleSearch} onReset={handleReset} />
          <List
            currentItems={currentItems}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </Fragment>
      )}
    </Fragment>
  );
};
export default LocationView;
